import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ControlTeaMachineRequest } from '@/lib/api/tea-machine-api'
import { teaMachineApi } from '@/lib/api/tea-machine-api'
import { ApiError } from '@/lib/api'
import { m } from '@/lib/paraglide/messages'
import { teaMachinesRefetchInterval } from '@/lib/tea-machines-poll-interval'

export const teaMachineKeys = {
    all: ['tea-machines'] as const,
    list: () => [...teaMachineKeys.all, 'list'] as const,
}

/**
 * Poll snapshot 36 máy. FE tự nội suy (dead reckoning) giữa 2 lần poll.
 *
 * `fresh: true` (màn điều khiển) → poll 4s + gọi thẳng SmartLab, KHÔNG dùng
 * cache server, để mỗi lượt poll phản ánh trạng thái thật.
 * Bỏ trống (máy trạm sản xuất) → GIỮ NGUYÊN như cũ: poll 8s + dùng cache 4s ở
 * server (che SmartLab khỏi 429 khi nhiều máy trạm cùng poll).
 */
export function useTeaMachines(opts?: { fresh?: boolean }) {
    const fresh = opts?.fresh ?? false
    const consecutiveFailures = useRef(0)
    // kind=config (SmartLab account rejected, integration off) does not heal
    // on a fast poll — see teaMachinesRefetchInterval (every 5 minutes).
    const lastErrorKind = useRef<string | undefined>(undefined)
    return useQuery({
        queryKey: fresh
            ? [...teaMachineKeys.list(), 'fresh']
            : teaMachineKeys.list(),
        queryFn: async () => {
            try {
                const data = (await teaMachineApi.list({ fresh })).data ?? []
                consecutiveFailures.current = 0
                lastErrorKind.current = undefined
                return data
            } catch (error) {
                consecutiveFailures.current += 1
                lastErrorKind.current =
                    error instanceof ApiError ? error.kind : undefined
                throw error
            }
        },
        refetchInterval: () =>
            teaMachinesRefetchInterval(
                {
                    failureCount: consecutiveFailures.current,
                    lastErrorKind: lastErrorKind.current,
                },
                { fresh },
            ),
        refetchOnWindowFocus: true,
        // The next poll is the retry; an immediate retry doubles outage noise.
        retry: 0,
    })
}

/**
 * Điều khiển máy (chạy/dừng/cài giờ). KHÔNG cập nhật lạc quan: sau khi lệnh gửi
 * xong, invalidate để lấy trạng thái THẬT từ server. BE đã xoá cache snapshot
 * ngay khi Control thành công, nên lần refetch này luôn hỏi SmartLab mới:
 *   - SmartLab đổi trạng thái ngay → UI cập nhật đúng tức thì.
 *   - SmartLab chưa đổi → UI giữ trạng thái cũ, nhịp poll 4s kế tiếp tự cập nhật.
 * Nhờ vậy không còn cảnh "nháy sang chạy rồi bật lại chưa chạy" như optimistic.
 */
export function useControlTeaMachine() {
    const qc = useQueryClient()

    return useMutation({
        mutationFn: (data: ControlTeaMachineRequest) =>
            teaMachineApi.control(data),
        onError: () => {
            toast.error(m.tea_machines_control_error())
        },
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: teaMachineKeys.all })
        },
    })
}
